import PropTypes from 'prop-types';
import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { Button, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleNotch } from '@fortawesome/free-solid-svg-icons';

import LoadingCircle from '../LoadingCircle';
import ModalLayout from '../ModalLayout';

// preload so bundler includes the files
import '../../Static/terms/8ef9fcdd-2e03-4743-be4d-609ddbdbbf6a';

export default memo(AcceptTermsAndConditionsModal);

const initState = {
    error: '',
    hasAcceptedTerms: [],
    org_id: '',
    requestPending: false,
  },
  logoutUrl = `${process.env['AUTH_PROTOCOL']}://${process.env['AUTH_HOST']}:${process.env['AUTH_PORT']}/logout`;

function AcceptTermsAndConditionsModal(props) {
  const {
    handleAllTermsAccepted,
    loginRes,
    loginRes: {
      hasAcceptedTerms,
      token,
      user: { _id: userID, Orgs },
    },
    toggleModal,
  } = props;

  const orgIdsToAccept = useMemo(
    () =>
      Orgs[0].path
        ?.split?.('.')
        ?.reverse?.()
        .filter?.((org_id) =>
          hasAcceptedTerms?.some?.((t) => t.org_id === org_id && !t.accepted)
        ) ?? [],
    [hasAcceptedTerms, Orgs]
  );

  const [state, updateState] = useState(() => ({
      ...initState,
      hasAcceptedTerms,
      org_id: orgIdsToAccept[0],
    })),
    [terms, setTerms] = useState([]);

  const currentAcceptingIndex = useMemo(
    () => orgIdsToAccept?.findIndex((id) => id === state.org_id),
    [orgIdsToAccept, state.org_id]
  );

  const handleAcceptClick = useCallback(async () => {
    try {
      updateState((prevState) => ({ ...prevState, requestPending: true }));

      const dateOfAcceptance = new Date().toISOString(),
        { terms_id: termsID } =
          state.hasAcceptedTerms?.find?.(
            ({ org_id }) => org_id === state.org_id
          ) ?? {},
        body = JSON.stringify({ dateOfAcceptance, termsID }),
        acceptTermsUrl = `${process.env['ACCOUNT_PROTOCOL']}://${process.env['ACCOUNT_HOST']}:${process.env['ACCOUNT_PORT']}/org/${state.org_id}/user/${userID}/terms`,
        requestOptions = {
          body,
          headers: { Authorization: token, 'Content-Type': 'application/json' },
          method: 'PUT',
        };

      const res = await window.fetch(acceptTermsUrl, requestOptions),
        apiError = res.ok ? null : await res.json();

      if (apiError) {
        updateState((prevState) => ({
          ...prevState,
          error: apiError.message,
          requestPending: false,
        }));
      } else {
        const updatedHasAcceptedTerms = state.hasAcceptedTerms.map((t) =>
          t.org_id === state.org_id
            ? { ...t, accepted: true, terms_id: termsID }
            : t
        );
        const allTermsAccepted = updatedHasAcceptedTerms.every(
          ({ accepted }) => accepted
        );

        if (allTermsAccepted) {
          handleAllTermsAccepted(loginRes);
        } else {
          const org_id = orgIdsToAccept[currentAcceptingIndex + 1];

          updateState((prevState) => ({
            ...prevState,
            hasAcceptedTerms: updatedHasAcceptedTerms,
            org_id,
            requestPending: false,
          }));
        }
      }
    } catch (err) {
      const error = err.response?.data?.message ?? err.message;
      updateState((prevState) => ({
        ...prevState,
        error,
        requestPending: false,
      }));
    }
  }, [
    currentAcceptingIndex,
    handleAllTermsAccepted,
    loginRes,
    orgIdsToAccept,
    state.hasAcceptedTerms,
    state.org_id,
    token,
    userID,
  ]);

  const handleDeclineClick = useCallback(async () => {
    try {
      updateState((prevState) => ({ ...prevState, requestPending: true }));
      await window.fetch(logoutUrl, {
        headers: { Authorization: token },
        method: 'POST',
      });
      toggleModal();
    } catch (err) {
      const error = err.response?.data?.message ?? err.message;
      updateState((prevState) => ({
        ...prevState,
        error,
        requestPending: false,
      }));
    }
  }, [toggleModal, token]);

  useEffect(() => {
    function fetchTerms() {
      const termsPromises = hasAcceptedTerms
        .filter(({ org_id }) => org_id === state.org_id)
        .map(async ({ terms_id }) => {
          const componentModule = await import(
            `../../Static/terms/${terms_id}`
          );
          const View = componentModule?.default ?? componentModule;
          return <View key={terms_id} />;
        });

      Promise.all(termsPromises).then(setTerms);
    }

    fetchTerms();
  }, [hasAcceptedTerms, state.org_id]);

  return (
    <ModalLayout
      backdrop="static"
      isOpen={props.isOpen}
      scrollable
      toggle={!state.requestPending && handleDeclineClick}
    >
      <ModalHeader className="border-0">
        Terms of Service (
        {`${currentAcceptingIndex + 1} of ${orgIdsToAccept.length}`})
      </ModalHeader>
      <ModalBody>
        {terms?.length ? terms : <LoadingCircle size="3x" />}
      </ModalBody>
      <ModalFooter>
        <p className="col-12 text-right text-danger nine-tenth-text">
          {state.error}
        </p>
        <Button
          className="rounded-lg three-quarter-text py-0 px-3"
          color="secondary"
          disabled={state.requestPending}
          onClick={handleDeclineClick}
          outline
        >
          Decline
        </Button>
        <button
          className="btn modal-button-submit rounded-lg three-quarter-text py-0 px-3"
          disabled={state.requestPending}
          onClick={handleAcceptClick}
        >
          {state.requestPending ? (
            <FontAwesomeIcon icon={faCircleNotch} spin />
          ) : (
            'Accept'
          )}
        </button>
      </ModalFooter>
    </ModalLayout>
  );
}

AcceptTermsAndConditionsModal.propTypes = {
  handleAllTermsAccepted: PropTypes.func,
  isOpen: PropTypes.bool,
  loginRes: PropTypes.shape({
    hasAcceptedTerms: PropTypes.arrayOf(
      PropTypes.shape({
        accepted: PropTypes.bool,
        org_id: PropTypes.string,
        terms_id: PropTypes.string,
      })
    ),
    token: PropTypes.string,
    user: PropTypes.object,
  }),
  toggleModal: PropTypes.func,
};
